import { FC, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Interface/Button/Button";
import { Link } from "@/components/Interface/Link/Link";
import { Notification } from "@/components/Interface/Notification/Notification";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { BillingAccountModal } from "@/components/Portal/Documents/BillingAccounts/BillingAccountModal/BillingAccountModal";
import { BillingAccountPickerModal } from "@/components/Portal/Documents/BillingAccounts/BillingAccountPicker/BillingAccountPickerModal";
import { BillingAccountSelectField } from "@/components/Portal/Documents/BillingAccounts/BillingAccountPicker/BillingAccountSelectField";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { DrAccount } from "@/services/model";

type OpenModal = "picker" | "create" | null;

interface Props {
  accounts: DrAccount[];
  selectedId?: number;
  disabled?: boolean;
  onChange: (accountId: number) => void;
}

/** "Abrechnung": the Vertragskonto the new Abo is billed to. */
export const CheckoutBillingAccount: FC<Props> = ({
  accounts,
  selectedId,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "Onboarding.sections.checkout.billingAccount",
  });
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const selectedAccount = accounts.find(
    (account) => account.id != null && account.id === selectedId,
  );

  return (
    <ProfileCard
      dataTestid="checkout-billing-account"
      variant="white"
      title={t("title")}
      description={t("description")}
    >
      {selectedAccount ? (
        <BillingAccountSelectField
          dataTestid="checkout-billing-account-select"
          account={selectedAccount}
          disabled={disabled}
          onClick={() => setOpenModal("picker")}
        />
      ) : (
        <Button
          dataTestid="checkout-billing-account-choose"
          fullWidth
          disabled={disabled}
          onClick={() => setOpenModal("picker")}
        >
          {t("choose")}
        </Button>
      )}
      {openModal === "picker" && (
        <BillingAccountPickerModal
          accounts={accounts}
          selectedId={selectedId}
          hint={
            <Notification
              dataTestid="checkout-billing-account-hint"
              inverted
              description={
                <Trans
                  i18nKey="Onboarding.sections.checkout.billingAccount.editHint"
                  components={[
                    <Link
                      key="billing-accounts-link"
                      type="button"
                      inverted
                      size="sm"
                      onClick={() => navigate(portalRoutes.documents.payments)}
                    />,
                  ]}
                />
              }
            />
          }
          onSelect={(accountId) => {
            onChange(accountId);
            setOpenModal(null);
          }}
          onCreate={() => setOpenModal("create")}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "create" && (
        <BillingAccountModal
          onClose={() => setOpenModal(null)}
          onCreated={onChange}
        />
      )}
    </ProfileCard>
  );
};
